import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useClaimReward } from './useClaimReward'

export function useClaimUSDT() {
  const queryClient = useQueryClient()
  const { loading, claimInvestmentUsdt } = useClaimReward()

  const refetchAll = () => {
    queryClient.invalidateQueries({ queryKey: ['myInvestments'] })
    queryClient.invalidateQueries({ queryKey: ['currentUser'] })
  }

  const mutateAsync = async (investmentId: string): Promise<string> => {
    try {
      const txid = await claimInvestmentUsdt(investmentId)
      toast.success(`USDT claimed! TX: ${txid.slice(0, 8)}...`)
      refetchAll()
      queryClient.invalidateQueries({ queryKey: ['investment', investmentId] })
      return txid
    } catch (err: any) {
      // Refetch to pick up claimLockedUntil from backend (shows lock timer)
      refetchAll()
      throw err
    }
  }

  const mutate = (investmentId: string) => {
    mutateAsync(investmentId).catch((err: any) => {
      toast.error(err.message || 'Failed to claim USDT')
    })
  }

  return { mutate, mutateAsync, isPending: loading }
}

export function useClaimTAKARA() {
  const queryClient = useQueryClient()
  const { loading, claimInvestmentTakara } = useClaimReward()

  const refetchAll = () => {
    queryClient.invalidateQueries({ queryKey: ['myInvestments'] })
    queryClient.invalidateQueries({ queryKey: ['currentUser'] })
  }

  const mutateAsync = async (investmentId: string): Promise<string> => {
    try {
      const txid = await claimInvestmentTakara(investmentId)
      toast.success(`TAKARA claimed! TX: ${txid.slice(0, 8)}...`)
      refetchAll()
      queryClient.invalidateQueries({ queryKey: ['investment', investmentId] })
      return txid
    } catch (err: any) {
      refetchAll()
      throw err
    }
  }

  const mutate = (investmentId: string) => {
    mutateAsync(investmentId).catch((err: any) => {
      toast.error(err.message || 'Failed to claim TAKARA')
    })
  }

  return { mutate, mutateAsync, isPending: loading }
}

export function useClaimAll() {
  const claimUSDT = useClaimUSDT()
  const claimTAKARA = useClaimTAKARA()

  const claimAllUSDT = async (investmentIds: string[]) => {
    for (const id of investmentIds) {
      await claimUSDT.mutateAsync(id)
    }
  }

  const claimAllTAKARA = async (investmentIds: string[]) => {
    for (const id of investmentIds) {
      await claimTAKARA.mutateAsync(id)
    }
  }

  return {
    claimAllUSDT,
    claimAllTAKARA,
    isLoading: claimUSDT.isPending || claimTAKARA.isPending,
  }
}
